import React, { useState } from "react";
import { Nav, Container, Row, Col, Button, OverlayTrigger, Tooltip } from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import { useAppContext } from "../context/appContext";
import { isViewOnly } from "../utils/permissions";
import { AddMeasurementTechModal } from "../pages/Measurements/MeasurementTech/UpdateTech/AddMeasurementTechModal";

const SubHeader = ({ env, loading, error, handleEnvChange, currentTenant, groups, groupVersions, currentGroupVersion }) => {
  const { user, activeKey, setActiveKey } = useAppContext();
  const navigate = useNavigate();
  const [showAddModal, setShowAddModal] = useState(false);

  const handleSelect = (selectedKey) => {
    setActiveKey(selectedKey);
    navigate(selectedKey);
  };

  const handleAddClick = () => {
    setShowAddModal(true);
  };

  const handleCloseAddModal = () => {
    setShowAddModal(false);
  };

  const username = user?.username;

  const currentTenantName = localStorage.getItem("selectedTenantName") || null;

  const isTenantSelected =
    currentTenantName !== null &&
    String(currentTenantName) !== "undefined" &&
    String(currentTenantName) !== "null" &&
    currentTenantName !== "";

  const isGroupVersionSelected =
    currentGroupVersion !== null &&
    String(currentGroupVersion) !== "undefined" &&
    String(currentGroupVersion) !== "null" &&
    currentGroupVersion !== "";

  const tenantHasGroups = isTenantSelected && groups?.length >= 1;
  const tenantHasGroupVersions = isTenantSelected && groupVersions?.length >= 1;

  const tenantHasGroupsAndVersionSelected = tenantHasGroups && isGroupVersionSelected;

  const enableSiteMeasurementButton = tenantHasGroupVersions && tenantHasGroupsAndVersionSelected;

  const projectsTabActive = activeKey === "/projects";
  const groupVersionsTabActive = activeKey === "/group-versions";
  const versionsCategoriesTabActive = activeKey === "/version-categories";

  const measurementsTabActive = activeKey === "/measurements";
  const measurementTechTabActive = activeKey === "/measurement-tech";

  // only show this secondary header when relevant primary headers are active in the main nav
  const projectsMainTabActive = projectsTabActive || groupVersionsTabActive || versionsCategoriesTabActive;
  const measurementsMainTabActive = measurementsTabActive || measurementTechTabActive;
  const showSecondaryHeader = measurementsMainTabActive || projectsMainTabActive;

  const isAllowedUser = !isViewOnly(user);

  const tenantHasProjectGroups = isTenantSelected && groups?.length >= 1;

  const enableVersionButton = isTenantSelected && tenantHasProjectGroups && groupVersionsTabActive;

  const renderTooltip = (props) => (
    <Tooltip id="button-tooltip" {...props}>
      Choose a tenant that has project groups
    </Tooltip>
  );

  const renderMeasurementTooltip = (props) => (
    <Tooltip id="button-tooltip" {...props}>
      Choose a tenant and ogmp group version
    </Tooltip>
  );

  if (!showSecondaryHeader) {
    return null;
  }

  return !loading && username ? (
    <Container fluid className="secondary-header-container mb-4 px-0">
      {measurementsMainTabActive && (
        <Row>
          <Col>
            <Nav
              variant="tabs"
              activeKey={activeKey}
              onSelect={handleSelect}
              className="secondary-nav w-100 d-flex justify-content-between"
              style={{ marginTop: "5px" }}
            >
              <div className="d-flex">
                <Nav.Item className="mr-3">
                  <Nav.Link eventKey="/measurements" style={{ fontSize: "1rem", color: "black" }}>
                    Site Measurements
                  </Nav.Link>
                </Nav.Item>
                <Nav.Item className="mx-3">
                  <Nav.Link eventKey="/measurement-tech" style={{ fontSize: "1rem", color: "black" }}>
                    Technologies
                  </Nav.Link>
                </Nav.Item>
                {isAllowedUser && measurementTechTabActive && (
                  <Button variant="primary" className="mt-1 mb-1 ms-4" onClick={() => handleAddClick()}>
                    Add Technology
                  </Button>
                )}
                {isAllowedUser && measurementsTabActive && !enableSiteMeasurementButton && (
                  <OverlayTrigger placement="top" delay={{ show: 250, hide: 400 }} overlay={renderMeasurementTooltip}>
                    {/* wrapping in a span because OverlayTrigger doesn't work well with disabled buttons */}
                    <span className="d-inline-block">
                      <Button variant="primary" className="text-white mt-1 mb-1 ms-4" style={{ pointerEvents: "none" }} disabled>
                        Add Measurement
                      </Button>
                    </span>
                  </OverlayTrigger>
                )}
                {isAllowedUser && measurementsTabActive && enableSiteMeasurementButton && (
                  <Button variant="primary" className="mt-1 mb-1 ms-4" onClick={() => navigate("/add-site-measurement")}>
                    Add Measurement
                  </Button>
                )}
              </div>
            </Nav>
          </Col>
        </Row>
      )}

      {projectsMainTabActive && (
        <Row>
          <Col>
            <Nav
              variant="tabs"
              activeKey={activeKey}
              onSelect={handleSelect}
              className="secondary-nav w-100 d-flex justify-content-between"
              style={{ marginTop: "5px" }}
            >
              <div className="d-flex">
                <Nav.Item className="mr-3">
                  <Nav.Link eventKey="/projects" style={{ fontSize: "1rem", color: "black" }}>
                    Project Groups
                  </Nav.Link>
                </Nav.Item>
                <Nav.Item className="mx-3">
                  <Nav.Link eventKey="/group-versions" style={{ fontSize: "1rem", color: "black" }}>
                    Group Versions
                  </Nav.Link>
                </Nav.Item>
                <Nav.Item className="mr-3 me-3">
                  <Nav.Link eventKey="/version-categories" style={{ fontSize: "1rem", color: "black" }}>
                    Version Categories
                  </Nav.Link>
                </Nav.Item>
                <div className="me-4" style={{ width: "20px" }}></div>
                {isAllowedUser && projectsTabActive && (
                  <Button variant="primary" className="mt-1 mb-1 ms-4" onClick={() => navigate("/add-project-group")}>
                    Add Group
                  </Button>
                )}
                {isAllowedUser && groupVersionsTabActive && !enableVersionButton && (
                  <OverlayTrigger placement="top" delay={{ show: 250, hide: 400 }} overlay={renderTooltip}>
                    {/* wrapping in a span because OverlayTrigger doesn't work well with disabled buttons */}
                    <span className="d-inline-block">
                      <Button variant="primary" className="text-white mt-1 mb-1 ms-4" style={{ pointerEvents: "none" }} disabled>
                        Add Version
                      </Button>
                    </span>
                  </OverlayTrigger>
                )}

                {isAllowedUser && groupVersionsTabActive && enableVersionButton && (
                  <Button variant="primary" className="mt-1 mb-1 ms-4" onClick={() => navigate("/add-group-version")}>
                    Add Version
                  </Button>
                )}
              </div>
            </Nav>
          </Col>
        </Row>
      )}
      <AddMeasurementTechModal
        show={showAddModal}
        handleClose={handleCloseAddModal}
        currentTenant={currentTenant}
        handleEnvChange={handleEnvChange}
        env={env}
      />
    </Container>
  ) : null;
};

export { SubHeader };
